import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Table from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/Table';
import {
  FDContentContainer, FDIcon, FDLabelText, FDStack,
} from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import TableBody from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableBody';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import { ScenarioSelectionDetailedSummaryTableHeader } from './ScenarioSelectionDetailedSummaryTableHeader';
import ScenarioSelectionDetailedSummarySectionAccordionRecursive from './ScenarioSelectionDetailedSummarySectionAccordionRecursive';

const scrollerObscuringSize = ({ theme, multiplier = 1 }) => ({ isMobile }) => theme.spacing(isMobile ? 3 : 6) * multiplier;

const useStyles = buildUseStyles('ScenarioSelectionDetailedSummaryLayout', ((theme) => ({
  root: {
    '& > .FD-MuiContainer-root': {
      transition: theme.transitions.create('max-width', {
        duration: theme.transitions.duration.standard,
      }),
    },
  },
  scroller: {
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  obscureRight: {
    pointerEvents: 'none',
    position: 'relative',
    '&:after': {
      content: "''",
      width: scrollerObscuringSize({ theme }),
      height: '100%',
      position: 'absolute',
      top: 0,
      left: scrollerObscuringSize({ theme, multiplier: -1 }),
      background: 'linear-gradient(90deg, rgba(246, 245, 240, 0), rgb(246, 245, 240))',
    },
  },
  extraSpacing: {
    minWidth: scrollerObscuringSize({ theme }),
  },
  table: {
    // By default, each <tr> gets a border below it.
    // If there's an empty row, it shouldn't have a border (that would look weird), nor should the thing above it have a border (that would also look weird).
    // Also, the final row shouldn't have a border below it
    '& tr[data-empty-row], & tr:has(+ tr[data-empty-row]), & tr:last-child': {
      '& td, & th': {
        border: 'none !important', // Must be important or else MUI's specificity makes this have no effect
      },
    },
  },
  denseTable: {
    '& td, & th': {
      padding: theme.spacing(0, 2),
    },
  },
  expandedTable: {
    // On a desktop, when this thing is expanded and you're looking at the 3 year view, it can take up too much width and look a bit awk.
    // This setting makes it feel much more reasonable.
    [theme.breakpoints.up('md')]: {
      minWidth: theme.breakpoints.values.md,
      width: 'fit-content',
      margin: '0 auto',
    },
  },
  mobileScrollText: {
    padding: theme.spacing(1, 0),
    alignSelf: 'flex-end',
  },
  invisible: {
    visibility: 'hidden',
  },
})));

const ScenarioSelectionDetailedSummarySection = ({
  headerLabel, columns, data, test, showMonthsProp, hasMaxWidth, dense,
}) => {
  const [showMonths, _setShowMonths] = React.useState(showMonthsProp);
  // If we're not being forced to show all months, then we're allowed to call setShowMonths
  const canSetShowMonths = !showMonthsProp;
  const setShowMonths = canSetShowMonths ? _setShowMonths : undefined;
  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });

  const actuallyShowingMonths = showMonthsProp || showMonths;

  const level = 0;

  let content = (
    <FDStack direction="column">
      {isMobile ? (
        <div
          className={classNames(classes.mobileScrollText, {
            [classes.invisible]: !actuallyShowingMonths,
          })}
        >
          <FDLabelText
            color="textDisabled"
            value={
              <FDStack alignItems="center">
                Scroll to view details <FDIcon icon="arrowRight" size="sm" color="textDisabled" />
              </FDStack>
            }
            align="end"
          />
        </div>
      ) : null}
      <FDStack direction="row">
        <div className={classes.scroller} {...dataAttrs(test)}>
          <Table
            className={classNames(classes.table, {
              [classes.expandedTable]: actuallyShowingMonths,
              [classes.denseTable]: dense,
            })}
          >
            <ScenarioSelectionDetailedSummaryTableHeader
              headerLabel={headerLabel}
              columns={columns}
              showMonths={showMonths}
              setShowMonths={setShowMonths}
              dense={dense}
            />
            <TableBody>
              <ScenarioSelectionDetailedSummarySectionAccordionRecursive
                data={data}
                test={`${test}-top-level`}
                level={level}
                showMonths={actuallyShowingMonths}
                dense={dense}
              />
            </TableBody>
          </Table>
          {/* Makes up for obscureRight + negative margin */}
          {actuallyShowingMonths ? <div className={classes.extraSpacing}>&nbsp;</div> : null}
        </div>
        {actuallyShowingMonths ? <div className={classes.obscureRight} /> : null}
      </FDStack>
    </FDStack>
  );

  if (hasMaxWidth) {
    content = <FDContentContainer maxWidth={actuallyShowingMonths ? 'xl' : 'md'}>{content}</FDContentContainer>;
  }

  return (
    <div className={classes.root}>
      {content}
    </div>
  );
};

ScenarioSelectionDetailedSummarySection.defaultProps = {
  test: 'detailed-scenario-table',
  showMonthsProp: false,
  hasMaxWidth: true,
};

ScenarioSelectionDetailedSummarySection.propTypes = {
  headerLabel: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  test: PropTypes.string,
  showMonthsProp: PropTypes.bool,
  hasMaxWidth: PropTypes.bool,
  dense: PropTypes.bool.isRequired,
};

export default ScenarioSelectionDetailedSummarySection;
